import { action, computed, makeObservable, observable } from 'mobx';
import { COMMON, MAIN } from '../configs/main.config';
import { RootStore } from './RootStore';
import { IInitialization } from '../pages/Initialization/Initialization';
import { TenantInfo } from '../services/solgari-api/interfaces/tenant-info';
import { FeatureFlags } from '@/services/solgari-api/interfaces/feature-flags.interface';

interface IDialerSettings {
  wrapUpTime: number;
}

export interface INotificationSettings {
  inboundCall: boolean;
  incomingMessage: boolean;
}

export interface IListSettings {
  dialer: IDialerSettings;
  notification: INotificationSettings;
}

export interface ITabSettings {
  currentTabId: string;
  activeTabId: string;
}

class SettingsStore {
  @observable
  list: IListSettings;

  @observable
  tabSettings: ITabSettings;

  @observable
  initializationState: IInitialization;

  @observable
  tenantInfo: TenantInfo | null;

  @observable
  featureFlags: FeatureFlags[] | null;

  @computed
  get dialer(): IDialerSettings {
    return this.list.dialer;
  }

  @computed
  get notification(): INotificationSettings {
    return this.list.notification;
  }

  @action
  setSettings = (list: Partial<IListSettings>) => {
    this.list = { ...this.list, ...list };

    const strList = JSON.stringify(this.list);

    localStorage.setItem(MAIN.LS_SETTINGS, strList);
  };

  @action
  setInitializationState = (getData: () => IInitialization) => {
    this.initializationState = getData();
    return this.initializationState;
  };

  @action
  setCurrentTabId = (id: string) => {
    this.tabSettings = {
      ...this.tabSettings,
      currentTabId: id,
    };
  };

  @action
  setActiveTabId = (id: string) => {
    this.tabSettings = {
      ...this.tabSettings,
      activeTabId: id,
    };
  };

  @action
  setTenantInfo = (info: TenantInfo) => {
    this.tenantInfo = info;
  };

  @action
  setFeatureFlags = (featureFlags: FeatureFlags[]) => {
    this.featureFlags = featureFlags;
  };

  constructor(public root: RootStore) {
    this.list = this.getSettingList();
    this.initializationState = {
      percent: 0,
      prompt: {
        dynScript: { text: 'Start initialization...', color: 'secondary' },
        dynLogin: { text: 'Start initialization...', color: 'secondary' },
        sipLogin: { text: 'Start initialization...', color: 'secondary' },
      },
    };
    this.tabSettings = this.getTabSettings();
    this.tenantInfo = null;
    this.featureFlags = null;
    makeObservable(this);
  }

  private getSettingList(): IListSettings {
    const listFromLSStr = localStorage.getItem(MAIN.LS_SETTINGS);
    const list: Partial<IListSettings> = JSON.parse(listFromLSStr) || {};

    const settings = this.assembleSetting(list);

    return settings;
  }

  private getTabSettings(): ITabSettings {
    return this.tabSettings;
  }

  private assembleSetting(list: Partial<IListSettings>): IListSettings {
    const settings: IListSettings = { ...COMMON.DEFAULT_SETTINGS };

    if (!Object.keys(list).length) {
      return settings;
    }

    if (list.dialer) {
      if (list.dialer.wrapUpTime) {
        settings.dialer.wrapUpTime = list.dialer.wrapUpTime;
      }
    }

    if (list.notification) {
      if (list.notification.inboundCall) {
        settings.notification.inboundCall = list.notification.inboundCall;
      }

      if (list.notification.incomingMessage) {
        settings.notification.incomingMessage =
          list.notification.incomingMessage;
      }
    }

    return settings;
  }
}

export default SettingsStore;
